// bm-design-system: result / outcome card
//
// A single client outcome in the app's card idiom: a header (icon + client, with
// optional context and a metric-category tag), a headline figure, a supporting
// label, and the detail in a faint inner panel. Styled with design tokens so it
// renders correctly on the light-locked marketing page and in the themeable admin
// design system. Adapted from a clean stat-card layout to fit real per-client data.
import * as React from "react";
import { TrendingUp } from "lucide-react";
import { cn } from "@/lib/utils";

export interface ResultCardProps extends React.HTMLAttributes<HTMLDivElement> {
  client: React.ReactNode;
  figure: React.ReactNode;
  figureLabel: React.ReactNode;
  detail?: React.ReactNode;
  /** Where the work happened, e.g. "US law firm, worked agency-side". */
  context?: React.ReactNode;
  /** Short metric category shown as a teal tag, e.g. "Conversion rate". */
  tag?: React.ReactNode;
  /** Client logo URL. When set, it fills the icon slot in place of the
      default TrendingUp mark. Falls back to the default icon when omitted. */
  logo?: string;
  /** Optional client quote. When set, the card renders it (client's voice,
      with attribution) in place of the third-person `detail` panel — turning a
      numbers-only result into a hybrid result + testimonial. */
  quote?: React.ReactNode;
  /** Who said the quote, e.g. "Chris Orr". */
  quoteName?: string;
  /** Their role / company, e.g. "CEO, NOVA". */
  quoteRole?: React.ReactNode;
  /** Headshot URL for the quote attribution. Omit for a name-only caption. */
  quoteImage?: string;
}

const ResultCard = React.forwardRef<HTMLDivElement, ResultCardProps>(
  (
    {
      client,
      figure,
      figureLabel,
      detail,
      context,
      tag,
      logo,
      quote,
      quoteName,
      quoteRole,
      quoteImage,
      className,
      ...props
    },
    ref,
  ) => (
    <div
      ref={ref}
      className={cn(
        "flex flex-col rounded-2xl border border-hairline bg-page p-6 shadow-sm",
        className,
      )}
      {...props}
    >
      {/* Logo + tag sit in the top corners on their own row, so the client name
          and context below get the full card width and don't wrap against the
          tag on narrow (carousel) cards. */}
      <div className="flex items-center justify-between gap-3">
        {logo ? (
          // Client logo chip. White tile + object-contain so marks with their own
          // backgrounds or odd aspect ratios sit uniformly. alt="" — the client
          // name sits right below, so it's decorative for SR.
          <span className="flex h-9 w-9 shrink-0 items-center justify-center overflow-hidden rounded-lg border border-hairline bg-white">
            <img src={logo} alt="" className="h-full w-full object-contain p-0.5" />
          </span>
        ) : (
          <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-accent-faded text-accent-display">
            <TrendingUp className="h-5 w-5" aria-hidden="true" />
          </span>
        )}
        {tag && <span className="badge badge-accent shrink-0">{tag}</span>}
      </div>

      <div className="mt-3">
        <strong className="block">{client}</strong>
        {/* Reserve two lines and clamp to two, so the sub-header never wraps to a
            third line and the headline figure below lines up across cards. */}
        <span className="mt-0.5 block min-h-10 text-sm text-ink-muted line-clamp-2">
          {context}
        </span>
      </div>

      <div className="mt-6 font-display text-4xl font-semibold tabular-nums text-ink-display">
        {figure}
      </div>
      {/* Reserve two lines so the panel/quote below starts at the same height
          across cards whether the label is one line or two. */}
      <div className="mt-2 min-h-10 text-sm text-ink-muted">{figureLabel}</div>

      {quote ? (
        // Hybrid: a client quote in their own voice. The figure grows to fill the
        // card; a fixed top gap keeps the quote TOPS aligned across a row, while
        // the spacer below the quote pushes the attribution to the card BOTTOM —
        // so tops and attributions both line up regardless of quote length. The
        // bare <blockquote> base style (teal left rule + italic) sets it apart
        // from the numbers-only cards' faint detail panel.
        <figure className="mt-6 flex grow flex-col">
          <blockquote>{quote}</blockquote>
          {(quoteName || quoteRole) && (
            <>
              <div aria-hidden="true" className="mt-6 grow" />
              <figcaption className="flex items-center gap-2.5">
                {quoteImage && (
                  <img
                    src={quoteImage}
                    alt=""
                    width={36}
                    height={36}
                    loading="lazy"
                    className="h-9 w-9 shrink-0 rounded-full border border-hairline object-cover"
                  />
                )}
                <span className="text-sm">
                  {quoteName && <strong>{quoteName}</strong>}
                  {quoteRole && (
                    <span className="block text-ink-muted">{quoteRole}</span>
                  )}
                </span>
              </figcaption>
            </>
          )}
        </figure>
      ) : (
        detail && (
          // Everything above is reserved to a consistent height, so the panel's
          // top aligns across the row; `grow` then stretches it to the card
          // bottom. Since the cards are equal height, every panel ends up the
          // same size regardless of how much text it holds.
          <div className="mt-6 grow rounded-xl bg-surface p-4">
            <p>{detail}</p>
          </div>
        )
      )}
    </div>
  ),
);
ResultCard.displayName = "ResultCard";

export { ResultCard };
